import { createFileRoute } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  CableIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  CircleCheckIcon,
  ClockIcon,
  CopyIcon,
  DownloadIcon,
  EditIcon,
  EyeIcon,
  EyeOffIcon,
  FolderIcon,
  Grid2x2Icon,
  GripVerticalIcon,
  HistoryIcon,
  InfoIcon,
  Layers2Icon,
  LightbulbIcon,
  Loader2Icon,
  LockIcon,
  MagnetIcon,
  MapPinIcon,
  MaximizeIcon,
  MinimizeIcon,
  MinusIcon,
  MoreHorizontalIcon,
  MoreVerticalIcon,
  MoveIcon,
  NetworkIcon,
  OctagonXIcon,
  PaletteIcon,
  PanelLeftIcon,
  PanelRightIcon,
  PencilIcon,
  PlugIcon,
  PlusIcon,
  Redo2Icon,
  RefreshCwIcon,
  RotateCcwIcon,
  RotateCwIcon,
  RulerIcon,
  SaveIcon,
  SearchIcon,
  SettingsIcon,
  ShapesIcon,
  SparklesIcon,
  SquareIcon,
  Trash2Icon,
  TriangleAlertIcon,
  TypeIcon,
  Undo2Icon,
  UnlockIcon,
  UploadIcon,
  WifiIcon,
  XIcon,
  ZapIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Text } from '@/components/ui/text';
import {
  Section,
  Tiles,
} from '@/features/design-system/showcase-primitives';

export const Route = createFileRoute('/design-system/icons')({
  component: IconsPage,
});

const NAVIGATION: ReadonlyArray<[LucideIcon, string]> = [
  [ArrowLeftIcon, 'arrow-left'],
  [ChevronLeftIcon, 'chevron-left'],
  [ChevronRightIcon, 'chevron-right'],
  [ChevronUpIcon, 'chevron-up'],
  [ChevronDownIcon, 'chevron-down'],
  [MoreHorizontalIcon, 'more-horizontal'],
  [MoreVerticalIcon, 'more-vertical'],
  [PanelLeftIcon, 'panel-left'],
  [PanelRightIcon, 'panel-right'],
];

const ACTIONS: ReadonlyArray<[LucideIcon, string]> = [
  [PlusIcon, 'plus'],
  [MinusIcon, 'minus'],
  [XIcon, 'x'],
  [CheckIcon, 'check'],
  [SaveIcon, 'save'],
  [PencilIcon, 'pencil'],
  [EditIcon, 'edit'],
  [Trash2Icon, 'trash-2'],
  [CopyIcon, 'copy'],
  [DownloadIcon, 'download'],
  [UploadIcon, 'upload'],
  [SearchIcon, 'search'],
];

const STATE: ReadonlyArray<[LucideIcon, string]> = [
  [InfoIcon, 'info'],
  [TriangleAlertIcon, 'triangle-alert'],
  [OctagonXIcon, 'octagon-x'],
  [CircleCheckIcon, 'circle-check'],
  [Loader2Icon, 'loader-2'],
  [EyeIcon, 'eye'],
  [EyeOffIcon, 'eye-off'],
  [LockIcon, 'lock'],
  [UnlockIcon, 'unlock'],
];

const EDITOR: ReadonlyArray<[LucideIcon, string]> = [
  [Grid2x2Icon, 'grid-2x2'],
  [RulerIcon, 'ruler'],
  [MagnetIcon, 'magnet'],
  [Layers2Icon, 'layers-2'],
  [MapPinIcon, 'map-pin'],
  [MoveIcon, 'move'],
  [GripVerticalIcon, 'grip-vertical'],
  [MaximizeIcon, 'maximize'],
  [MinimizeIcon, 'minimize'],
  [RotateCcwIcon, 'rotate-ccw'],
  [RotateCwIcon, 'rotate-cw'],
  [Undo2Icon, 'undo-2'],
  [Redo2Icon, 'redo-2'],
  [RefreshCwIcon, 'refresh-cw'],
  [HistoryIcon, 'history'],
  [ClockIcon, 'clock'],
];

const DOMAIN: ReadonlyArray<[LucideIcon, string, string]> = [
  [LightbulbIcon, 'lightbulb', 'Light items'],
  [PlugIcon, 'plug', 'Socket items'],
  [NetworkIcon, 'network', 'Network items'],
  [WifiIcon, 'wifi', 'Wi-Fi access point'],
  [CableIcon, 'cable', 'RJ45 ports'],
  [ZapIcon, 'zap', 'Electrical (generic)'],
  [SquareIcon, 'square', 'Outlet (generic)'],
  [FolderIcon, 'folder', 'Plan'],
  [PaletteIcon, 'palette', 'Room color'],
  [ShapesIcon, 'shapes', 'Polygon room'],
  [SparklesIcon, 'sparkles', 'Brand'],
  [TypeIcon, 'type', 'Text'],
  [SettingsIcon, 'settings', 'Settings'],
];

const GLYPHS: ReadonlyArray<[string, string, string]> = [
  ['✕', 'close', 'Close panels and confirm-cancel rows'],
  ['✎', 'rename', 'Rename inline (room/plan/floor names)'],
  ['⬠', 'polygon', 'Polygon draw (rooms)'],
  ['⬟', 'polygon-redraw', 'Polygon redraw'],
  ['←', 'back', 'Back to plans'],
  ['+', 'add', 'Additive action prefix (+ New, + Add new)'],
  ['…', 'in-progress', 'Trailing ellipsis on labels (Saving…)'],
];

function IconsPage() {
  return (
    <div className="flex flex-col gap-14">
      <header className="flex flex-col gap-2">
        <Text variant="eyebrow">Icons</Text>
        <Text variant="display" as="h1">
          Lucide & glyphs
        </Text>
        <Text variant="body" tone="muted" className="max-w-2xl">
          Lucide is the icon library — monochrome, 1.5px stroke,{' '}
          <code className="font-mono">currentColor</code>. Icons inherit text
          color and size with the surrounding context. The codebase also uses a
          small set of inline Unicode glyphs for compact actions; treat those
          as part of the language.
        </Text>
      </header>

      <Section
        eyebrow="Lucide"
        title="Navigation"
        description="Where the user is going. Used in headers, rails, and back affordances."
      >
        <IconGrid items={NAVIGATION} />
      </Section>

      <Section
        eyebrow="Lucide"
        title="Actions"
        description="What the user can do. Pair with a label in chrome; icon-only is reserved for dense toolbars."
      >
        <IconGrid items={ACTIONS} />
      </Section>

      <Section
        eyebrow="Lucide"
        title="State"
        description="What's true right now. Drives Sonner toaster icons and inline status badges."
      >
        <IconGrid items={STATE} />
      </Section>

      <Section
        eyebrow="Lucide"
        title="Editor"
        description="Specific to the canvas — grid, snapping, panning, undo/redo."
      >
        <IconGrid items={EDITOR} />
      </Section>

      <Section
        eyebrow="Lucide"
        title="Domain"
        description="Mapped to Lights Planner concepts — Light / Socket / Network and the rest of the placement vocabulary."
      >
        <Tiles cols={4}>
          {DOMAIN.map(([Icon, name, hint]) => (
            <div
              key={name}
              className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3"
            >
              <Icon className="size-5 text-foreground" />
              <Text variant="label">{name}</Text>
              <Text variant="caption">{hint}</Text>
            </div>
          ))}
        </Tiles>
      </Section>

      <Section
        eyebrow="Inline"
        title="Unicode glyphs"
        description="Used inline in the codebase as compact action stand-ins. They're explicit choices — not lazy fallbacks. Replace with a Lucide icon only when surrounding density allows."
      >
        <Tiles cols={4}>
          {GLYPHS.map(([glyph, name, hint]) => (
            <div
              key={name}
              className="flex flex-col items-start gap-2 rounded-lg border border-border bg-card p-3"
            >
              <span
                aria-hidden
                className="font-mono text-2xl leading-none text-foreground"
              >
                {glyph}
              </span>
              <div className="flex flex-col gap-0.5">
                <Text variant="label">{name}</Text>
                <Text variant="caption">{hint}</Text>
              </div>
            </div>
          ))}
        </Tiles>
      </Section>
    </div>
  );
}

function IconGrid({
  items,
}: {
  items: ReadonlyArray<[LucideIcon, string]>;
}) {
  return (
    <Tiles cols={6}>
      {items.map(([Icon, name]) => (
        <div
          key={name}
          className="flex flex-col items-center gap-2 rounded-lg border border-border bg-card p-3"
        >
          <Icon className="size-5 text-foreground" />
          <Text variant="code" tone="muted" className="text-center">
            {name}
          </Text>
        </div>
      ))}
    </Tiles>
  );
}
